/**
 * Industry presets applied at company onboarding. Everything here is copied
 * into the tenant's own settings, so the company can change it afterwards
 * without affecting other tenants.
 */

export const INDUSTRY_KEYS = [
  'it_software',
  'digital_marketing',
  'web_agency',
  'healthcare_admin',
  'clinic_diagnostics',
  'education',
  'consultancy',
  'trading',
  'retail',
  'construction_real_estate',
  'other',
] as const;
export type IndustryKey = (typeof INDUSTRY_KEYS)[number];

export interface Term {
  singular: string;
  plural: string;
}

export interface Terminology {
  client: Term;
  lead: Term;
  employee: Term;
  task: Term;
}

export interface LeadStageConfig {
  key: string;
  label: string;
  color: string;
  order: number;
  isWon?: boolean;
  isLost?: boolean;
}

export interface LeadSourceConfig {
  key: string;
  label: string;
}

export interface IndustryPreset {
  key: IndustryKey;
  label: string;
  terminology: Terminology;
  departments: string[];
  designations: { name: string; department?: string }[];
  leadStages: LeadStageConfig[];
  leadSources: LeadSourceConfig[];
  taskCategories: string[];
}

const DEFAULT_TERMS: Terminology = {
  client: { singular: 'Client', plural: 'Clients' },
  lead: { singular: 'Lead', plural: 'Leads' },
  employee: { singular: 'Employee', plural: 'Employees' },
  task: { singular: 'Task', plural: 'Tasks' },
};

export const DEFAULT_LEAD_STAGES: LeadStageConfig[] = [
  { key: 'new', label: 'New', color: '#64748b', order: 1 },
  { key: 'contacted', label: 'Contacted', color: '#0ea5e9', order: 2 },
  { key: 'qualified', label: 'Qualified', color: '#8b5cf6', order: 3 },
  { key: 'proposal', label: 'Proposal', color: '#f59e0b', order: 4 },
  { key: 'negotiation', label: 'Negotiation', color: '#f97316', order: 5 },
  { key: 'won', label: 'Won', color: '#10b981', order: 6, isWon: true },
  { key: 'lost', label: 'Lost', color: '#ef4444', order: 7, isLost: true },
];

export const DEFAULT_LEAD_SOURCES: LeadSourceConfig[] = [
  { key: 'website', label: 'Website' },
  { key: 'referral', label: 'Referral' },
  { key: 'social_media', label: 'Social media' },
  { key: 'cold_call', label: 'Cold call' },
  { key: 'email_campaign', label: 'Email campaign' },
  { key: 'walk_in', label: 'Walk-in' },
  { key: 'advertisement', label: 'Advertisement' },
  { key: 'other', label: 'Other' },
];

function preset(p: Partial<IndustryPreset> & Pick<IndustryPreset, 'key' | 'label'>): IndustryPreset {
  return {
    terminology: DEFAULT_TERMS,
    departments: ['Management', 'Sales', 'Operations', 'Human Resources', 'Accounts'],
    designations: [
      { name: 'Director', department: 'Management' },
      { name: 'Sales Executive', department: 'Sales' },
      { name: 'Operations Executive', department: 'Operations' },
      { name: 'HR Executive', department: 'Human Resources' },
      { name: 'Accountant', department: 'Accounts' },
    ],
    leadStages: DEFAULT_LEAD_STAGES,
    leadSources: DEFAULT_LEAD_SOURCES,
    taskCategories: ['General', 'Follow-up', 'Administrative'],
    ...p,
  };
}

export const INDUSTRY_PRESETS: Record<IndustryKey, IndustryPreset> = {
  it_software: preset({
    key: 'it_software',
    label: 'IT and Software Development',
    departments: ['Management', 'Sales', 'Engineering', 'Quality Assurance', 'Design', 'Human Resources', 'Accounts', 'Support'],
    designations: [
      { name: 'Software Developer', department: 'Engineering' },
      { name: 'Senior Software Developer', department: 'Engineering' },
      { name: 'Technical Lead', department: 'Engineering' },
      { name: 'QA Engineer', department: 'Quality Assurance' },
      { name: 'UI/UX Designer', department: 'Design' },
      { name: 'Business Development Executive', department: 'Sales' },
      { name: 'Telecaller', department: 'Sales' },
      { name: 'HR Executive', department: 'Human Resources' },
      { name: 'Accountant', department: 'Accounts' },
      { name: 'Support Engineer', department: 'Support' },
    ],
    taskCategories: ['Development', 'Bug fix', 'Code review', 'Testing', 'Deployment', 'Meeting', 'Documentation'],
  }),
  digital_marketing: preset({
    key: 'digital_marketing',
    label: 'Digital Marketing Agency',
    departments: ['Management', 'Sales', 'SEO', 'Social Media', 'Content', 'Paid Ads', 'Design', 'Accounts'],
    designations: [
      { name: 'SEO Specialist', department: 'SEO' },
      { name: 'Social Media Manager', department: 'Social Media' },
      { name: 'Content Writer', department: 'Content' },
      { name: 'Performance Marketer', department: 'Paid Ads' },
      { name: 'Graphic Designer', department: 'Design' },
      { name: 'Sales Executive', department: 'Sales' },
    ],
    taskCategories: ['Campaign', 'Content', 'Creative', 'Reporting', 'Client call'],
  }),
  web_agency: preset({
    key: 'web_agency',
    label: 'Web Development Agency',
    departments: ['Management', 'Sales', 'Development', 'Design', 'Support', 'Accounts'],
    designations: [
      { name: 'Web Developer', department: 'Development' },
      { name: 'Frontend Developer', department: 'Development' },
      { name: 'Web Designer', department: 'Design' },
      { name: 'Sales Executive', department: 'Sales' },
      { name: 'Support Executive', department: 'Support' },
    ],
    taskCategories: ['Development', 'Design', 'Content upload', 'Bug fix', 'Hosting'],
  }),
  healthcare_admin: preset({
    key: 'healthcare_admin',
    label: 'Hospital and Healthcare Administration',
    terminology: {
      ...DEFAULT_TERMS,
      client: { singular: 'Patient', plural: 'Patients' },
      lead: { singular: 'Enquiry', plural: 'Enquiries' },
      employee: { singular: 'Staff member', plural: 'Staff' },
    },
    departments: ['Administration', 'Front Office', 'Nursing', 'Billing', 'Human Resources', 'Housekeeping'],
    designations: [
      { name: 'Front Office Executive', department: 'Front Office' },
      { name: 'Administrator', department: 'Administration' },
      { name: 'Nurse', department: 'Nursing' },
      { name: 'Billing Executive', department: 'Billing' },
      { name: 'HR Executive', department: 'Human Resources' },
    ],
    leadStages: [
      { key: 'new', label: 'New enquiry', color: '#64748b', order: 1 },
      { key: 'contacted', label: 'Contacted', color: '#0ea5e9', order: 2 },
      { key: 'appointment_booked', label: 'Appointment booked', color: '#8b5cf6', order: 3 },
      { key: 'won', label: 'Registered', color: '#10b981', order: 4, isWon: true },
      { key: 'lost', label: 'Not interested', color: '#ef4444', order: 5, isLost: true },
    ],
    taskCategories: ['Administrative', 'Follow-up', 'Billing', 'Facility'],
  }),
  clinic_diagnostics: preset({
    key: 'clinic_diagnostics',
    label: 'Clinic and Diagnostic Centre',
    terminology: {
      ...DEFAULT_TERMS,
      client: { singular: 'Patient', plural: 'Patients' },
      lead: { singular: 'Enquiry', plural: 'Enquiries' },
      employee: { singular: 'Staff member', plural: 'Staff' },
    },
    departments: ['Front Desk', 'Laboratory', 'Radiology', 'Billing', 'Administration'],
    designations: [
      { name: 'Receptionist', department: 'Front Desk' },
      { name: 'Lab Technician', department: 'Laboratory' },
      { name: 'Radiology Technician', department: 'Radiology' },
      { name: 'Billing Executive', department: 'Billing' },
    ],
    taskCategories: ['Administrative', 'Sample collection', 'Report delivery', 'Follow-up'],
  }),
  education: preset({
    key: 'education',
    label: 'Education and Training',
    terminology: {
      ...DEFAULT_TERMS,
      client: { singular: 'Student', plural: 'Students' },
      lead: { singular: 'Enquiry', plural: 'Enquiries' },
    },
    departments: ['Administration', 'Admissions', 'Faculty', 'Accounts', 'Placement'],
    designations: [
      { name: 'Admission Counsellor', department: 'Admissions' },
      { name: 'Trainer', department: 'Faculty' },
      { name: 'Coordinator', department: 'Administration' },
      { name: 'Placement Officer', department: 'Placement' },
    ],
    leadStages: [
      { key: 'new', label: 'New enquiry', color: '#64748b', order: 1 },
      { key: 'counselled', label: 'Counselled', color: '#0ea5e9', order: 2 },
      { key: 'demo_attended', label: 'Demo attended', color: '#8b5cf6', order: 3 },
      { key: 'won', label: 'Enrolled', color: '#10b981', order: 4, isWon: true },
      { key: 'lost', label: 'Dropped', color: '#ef4444', order: 5, isLost: true },
    ],
    taskCategories: ['Class', 'Admissions', 'Evaluation', 'Administrative'],
  }),
  consultancy: preset({
    key: 'consultancy',
    label: 'Consultancy',
    departments: ['Management', 'Consulting', 'Business Development', 'Research', 'Accounts'],
    designations: [
      { name: 'Consultant', department: 'Consulting' },
      { name: 'Senior Consultant', department: 'Consulting' },
      { name: 'Business Development Manager', department: 'Business Development' },
      { name: 'Research Analyst', department: 'Research' },
    ],
  }),
  trading: preset({
    key: 'trading',
    label: 'Trading and Distribution',
    departments: ['Management', 'Sales', 'Purchase', 'Warehouse', 'Logistics', 'Accounts'],
    designations: [
      { name: 'Sales Executive', department: 'Sales' },
      { name: 'Purchase Executive', department: 'Purchase' },
      { name: 'Warehouse Supervisor', department: 'Warehouse' },
      { name: 'Logistics Coordinator', department: 'Logistics' },
    ],
    taskCategories: ['Order', 'Dispatch', 'Collection', 'Vendor follow-up'],
  }),
  retail: preset({
    key: 'retail',
    label: 'Retail and E-commerce',
    terminology: { ...DEFAULT_TERMS, client: { singular: 'Customer', plural: 'Customers' } },
    departments: ['Management', 'Store Operations', 'E-commerce', 'Customer Service', 'Inventory', 'Accounts'],
    designations: [
      { name: 'Store Manager', department: 'Store Operations' },
      { name: 'Sales Associate', department: 'Store Operations' },
      { name: 'E-commerce Executive', department: 'E-commerce' },
      { name: 'Customer Service Executive', department: 'Customer Service' },
    ],
  }),
  construction_real_estate: preset({
    key: 'construction_real_estate',
    label: 'Construction and Real Estate',
    terminology: { ...DEFAULT_TERMS, client: { singular: 'Customer', plural: 'Customers' } },
    departments: ['Management', 'Sales', 'Site Operations', 'Engineering', 'Procurement', 'Accounts'],
    designations: [
      { name: 'Site Engineer', department: 'Site Operations' },
      { name: 'Project Engineer', department: 'Engineering' },
      { name: 'Sales Executive', department: 'Sales' },
      { name: 'Procurement Officer', department: 'Procurement' },
    ],
    leadStages: [
      { key: 'new', label: 'New', color: '#64748b', order: 1 },
      { key: 'site_visit', label: 'Site visit', color: '#0ea5e9', order: 2 },
      { key: 'negotiation', label: 'Negotiation', color: '#f97316', order: 3 },
      { key: 'won', label: 'Booked', color: '#10b981', order: 4, isWon: true },
      { key: 'lost', label: 'Lost', color: '#ef4444', order: 5, isLost: true },
    ],
  }),
  other: preset({ key: 'other', label: 'Other / Custom Business' }),
};

export const INDUSTRY_OPTIONS = INDUSTRY_KEYS.map((key) => ({ value: key, label: INDUSTRY_PRESETS[key].label }));
